// Nominations: speeches, then a clockwise ballot. Each phone watches the vote travel around the
// circle; the current voter's phone turns into a full-screen YES / NO.
import { useState } from "react";
import { Countdown, CountdownRing } from "../components/Countdown";
import { PhaseHeader } from "../components/PhaseHeader";
import { TownSquare } from "../components/TownSquare";
import { useCommand } from "../hooks/useCommand";
import type { NominationView, PlayerView } from "../protocol";
import { outcomeText, seatName, voteTally } from "../state/selectors";
import { useGame } from "../state/store";

export function NominationScreen({ view }: { view: PlayerView }) {
  const roles = useGame((s) => s.roles);
  const { run, busy } = useCommand();
  const day = view.day!;
  const nom = day.current_nomination!;
  const me = view.me_seat_id;
  const speaker = me === nom.nominator_seat_id || me === nom.nominee_seat_id;
  const tally = voteTally(nom, day.block_votes, day.threshold);

  return (
    <main className="screen">
      <PhaseHeader view={view} roles={roles} />
      <NominationTitle view={view} nom={nom} />

      {nom.stage === "speeches" && (
        <TownSquare view={view} nomination={nom} blockSeatId={day.block?.seat_id ?? null}>
          <span className="eyebrow">To execute</span>
          <strong style={{ fontSize: "2rem" }}>{tally.needed}</strong>
          <span className="small muted">{tally.needed === 1 ? "vote" : "votes"} needed</span>
        </TownSquare>
      )}

      {nom.stage === "speeches" && (
        <div className="card stack center">
          <span className="eyebrow">Speeches</span>
          {nom.nominator_seat_id === nom.nominee_seat_id ? (
            <p>{seatName(view, nom.nominee_seat_id)} nominated themselves, and says why.</p>
          ) : (
            <p>
              {seatName(view, nom.nominator_seat_id)} says why, then{" "}
              {seatName(view, nom.nominee_seat_id)} answers.
            </p>
          )}
          <Countdown deadline={nom.speech_deadline_ms} prefix="Vote starts in " className="muted" />
          {speaker && !nom.speech_done.includes(me) && (
            <button
              className="btn"
              disabled={busy}
              onClick={() => run({ type: "end_speech" })}
            >
              I&apos;m done speaking
            </button>
          )}
          {speaker && nom.speech_done.includes(me) && (
            <p className="small muted">Waiting for the other speaker…</p>
          )}
        </div>
      )}

      {nom.stage !== "speeches" && (
        <>
          <TownSquare view={view} nomination={nom} blockSeatId={day.block?.seat_id ?? null}>
            <span className="eyebrow">Yes votes</span>
            <strong style={{ fontSize: "2rem" }}>{tally.yes}</strong>
            <span className="small muted">{tally.needed} needed</span>
          </TownSquare>
          <BallotStatus view={view} nom={nom} />
        </>
      )}
    </main>
  );
}

function NominationTitle({ view, nom }: { view: PlayerView; nom: NominationView }) {
  return (
    <div className="stack-sm center">
      <span className="eyebrow">Nomination</span>
      <h2>
        {seatName(view, nom.nominator_seat_id)} <span className="muted">→</span>{" "}
        <span className="evil">{seatName(view, nom.nominee_seat_id)}</span>
      </h2>
    </div>
  );
}

function BallotStatus({ view, nom }: { view: PlayerView; nom: NominationView }) {
  const me = view.me_seat_id;
  if (nom.stage === "resolved") {
    return <p className="card card-tight center">{outcomeText(view, nom)}.</p>;
  }
  const current = nom.current_voter_seat_id;
  const myVote = nom.votes.find((v) => v.seat_id === me);
  const myIndex = nom.voter_order.indexOf(me);
  const currentIndex = current ? nom.voter_order.indexOf(current) : -1;
  let mine: string;
  if (!myVote) mine = "You cannot vote on this one (no ghost vote left).";
  else if (myVote.struck) mine = "Your master voted no, so your yes does not count.";
  else if (myVote.yes === true) mine = myVote.auto ? "Time ran out. You voted yes." : "You voted yes.";
  else if (myVote.yes === false) mine = myVote.auto ? "Time ran out: counted as no." : "You voted no.";
  else if (myIndex > currentIndex) {
    const before = myIndex - currentIndex;
    mine = before === 1 ? "You vote next." : `You vote in ${before} turns.`;
  } else mine = "";

  return (
    <div className="card card-tight stack-sm center">
      {current && (
        <p>
          Now voting: <strong>{seatName(view, current)}</strong>{" "}
          <Countdown deadline={nom.turn_deadline_ms} className="muted mono" />
        </p>
      )}
      {mine && <p className="small muted">{mine}</p>}
      <p className="tiny faint">
        Hands go up clockwise from {seatName(view, nom.voter_order[0])}; the nominee votes last.
      </p>
    </div>
  );
}

export function VoteTurnScreen({ view }: { view: PlayerView }) {
  const { run, busy } = useCommand();
  const day = view.day!;
  const nom = day.current_nomination!;
  const priv = view.private;
  const tally = voteTally(nom, day.block_votes);
  const me = view.seats.find((s) => s.seat_id === view.me_seat_id);
  const ghost = me && !me.alive;
  const vote = (yes: boolean) =>
    run({ type: "cast_vote", nomination_id: nom.nomination_id, yes });

  return (
    <main className="screen vote-turn">
      <div className="stack center" style={{ marginTop: 12 }}>
        <span className="eyebrow">Your vote</span>
        <h1>
          Execute <span className="evil">{seatName(view, nom.nominee_seat_id)}</span>?
        </h1>
        <p className="muted">
          {tally.yes} yes so far · {tally.needed} needed
        </p>
      </div>
      <div className="row" style={{ justifyContent: "center" }}>
        <CountdownRing deadline={nom.turn_deadline_ms} totalMs={view.config.vote_seconds * 1000} />
      </div>
      {ghost && priv.can_vote_yes && (
        <p className="center small">Voting yes uses your one ghost vote.</p>
      )}
      {priv.vote_restriction && <p className="center small muted">{priv.vote_restriction}</p>}
      {priv.vote_condition && <p className="center small">{priv.vote_condition}</p>}
      <div className="grow" />
      <div className="vote-buttons">
        <button className="btn btn-no" disabled={busy} onClick={() => vote(false)}>
          No
        </button>
        <button
          className="btn btn-yes"
          disabled={busy || !priv.can_vote_yes}
          onClick={() => vote(true)}
        >
          Yes
        </button>
      </div>
      <p className="tiny faint center">Raise your hand as you vote yes. No answer counts as no.</p>
    </main>
  );
}

export function ExecutionScreen({ view }: { view: PlayerView }) {
  const roles = useGame((s) => s.roles);
  const execution = view.day?.execution;
  const executed = execution?.executed_seat_id ?? null;
  let headline = "Nobody was executed today.";
  if (executed && execution?.kind === "virgin") {
    headline = `${seatName(view, executed)} nominated the Virgin and is executed at once.`;
  } else if (executed && execution?.died === false) {
    headline = `${seatName(view, executed)} is executed, but does not die.`;
  } else if (executed) {
    headline = `${seatName(view, executed)} is executed.`;
  }
  const choices = view.day?.death_choices ?? [];
  return (
    <main className="screen">
      <PhaseHeader view={view} roles={roles} />
      <div className="grow stack center" style={{ justifyContent: "center" }}>
        <span className="eyebrow">Dusk</span>
        <h2>{headline}</h2>
        {choices.map((c) => (
          <p key={c.seat_id}>
            {seatName(view, c.seat_id)} chooses{" "}
            {c.target_seat_id ? seatName(view, c.target_seat_id) : "no one"}.
          </p>
        ))}
        {view.private.can_death_choice ? (
          <DuskChoice view={view} />
        ) : (
          <>
            <p className="muted">Night is falling. Eyes on your own phone.</p>
            {execution?.night_at_ms && !choices.length && executed && (
              <p className="small faint">
                <Countdown deadline={execution.night_at_ms} prefix="Night in " />
              </p>
            )}
          </>
        )}
      </div>
    </main>
  );
}

/** The executed player may publicly choose a living player before night falls (the
 * Moonchild's ability). Every executed player is offered it, so it gives nothing away. The
 * Moonchild's own phone (`death_choice_required`) offers no "no one": they must choose. */
function DuskChoice({ view }: { view: PlayerView }) {
  const { run, busy } = useCommand();
  const [picked, setPicked] = useState<string | null>(null);
  const living = view.seats.filter((s) => s.alive && s.seat_id !== view.me_seat_id);
  const deadline = view.day?.execution?.night_at_ms ?? null;
  const required = view.private.death_choice_required;
  return (
    <div className="stack" style={{ width: "100%" }}>
      {required ? (
        <p>
          You are the <strong>Moonchild</strong>, so you must choose a living player. Say out
          loud who you choose, and choose them here. If they are good, they die tonight. If time
          runs out, the app chooses for you at random.
        </p>
      ) : (
        <p>
          If you are the <strong>Moonchild</strong>, say out loud which living player you choose,
          and choose them here. If they are good, they die tonight.
        </p>
      )}
      <TownSquare
        view={view}
        candidates={living.map((s) => s.seat_id)}
        selected={picked ? [picked] : []}
        onSeatTap={(id) => setPicked((cur) => (cur === id ? null : id))}
      >
        <span className="small">{picked ? seatName(view, picked) : "Tap a player"}</span>
      </TownSquare>
      <button
        className="btn btn-primary"
        disabled={!picked || busy}
        onClick={() => run({ type: "death_choice", target_seat_id: picked })}
      >
        {picked ? `Choose ${seatName(view, picked)}` : "Choose a player"}
      </button>
      {!required && (
        <button
          className="btn btn-ghost"
          disabled={busy}
          onClick={() => run({ type: "death_choice", target_seat_id: null })}
        >
          Choose no one
        </button>
      )}
      <p className="tiny faint">
        Anyone executed may choose; only the Moonchild&apos;s choice does anything.{" "}
        {deadline && <Countdown deadline={deadline} prefix="Night falls in " />}
      </p>
    </div>
  );
}
